import { ComponentRendering } from '@sitecore-jss/sitecore-jss-react';
import {
  TContentHubModel,
  TContentHubModelDetailSpecificationsTableFields,
  TContentHubModelDetailSpecificationsTableProps,
  TExportData,
  TMRPRow,
  TSpecGroup,
  TTableData,
} from '../types/index.types';
import { StringField, TContentHubButton, TIconFromRenderingParams, TStringField } from '../../../types/types';
import { ComponentParams } from '@sitecore-jss/sitecore-jss-react';
import { BACKGROUND_COLOR_MAP, BACKGROUND_COLOR_CLASS_MAP } from '../../../constants/background-color.constants';
import { APIPrefix } from '../../../constants/api.constants';
import { appendRootToRichTextInternalLinks } from '../../../utils/appendRootToRichTextInternalLinks.utils';

const createSpecsObj = (models?: TContentHubModel[]): TTableData => {
  const specs: TTableData = {};
  models?.forEach((model) => {
    model.specs.forEach((spec) => {
      if (spec.group && spec.items) {
        if (!specs[spec.group]) {
          specs[spec.group] = {
            group: '',
            tempItems: {},
            items: [],
          };
        }
        specs[spec.group].group = spec.group;
        if (!specs[spec.group].tempItems) {
          specs[spec.group].tempItems = {};
        }
        spec.items.forEach((item) => {
          if (specs?.[spec.group]?.tempItems && !specs?.[spec.group]?.tempItems?.[item.id]) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            specs[spec.group].tempItems![item.id] = {
              id: item.id,
              key: item.key,
              specs: [],
            };
          }
          specs[spec.group]?.tempItems?.[item.id].specs.push(item);
        });
      }
    });
  });
  return specs;
};

export class ContentHubModelDetailSpecificationsTableModel {
  private readonly fields?: TContentHubModelDetailSpecificationsTableFields;
  private readonly params?: ComponentParams;
  private readonly rendering?: ComponentRendering;
  private readonly seriesDetail: boolean;

  constructor(props: TContentHubModelDetailSpecificationsTableProps) {
    this.fields = props?.fields || null;
    this.rendering = props?.rendering;
    this.params = props?.params || undefined;
    this.seriesDetail = props?.seriesDetail || false;
  }

  get contextItemUrl(): string {
    return this.fields?.itemData?.contextItemUrl || '';
  }

  get backgroundColor(): string | undefined {
    return BACKGROUND_COLOR_MAP.get(this.params?.backgroundColor || '');
  }

  get backgroundColorClass(): string {
    return BACKGROUND_COLOR_CLASS_MAP.get(this.params?.backgroundColor || '') || '';
  }

  get thumbnailEE(): string {
    return this.fields?.itemData?.thumbnail || '';
  }

  get componentName(): string {
    return this.rendering?.componentName || '';
  }

  get title(): StringField {
    return { value: this.fields?.apiData?.title || '' };
  }

  get eyebrow(): StringField {
    return { value: this.fields?.apiData?.eyebrow || '' };
  }

  get description(): StringField {
    return { value: this.fields?.apiData?.description || '' };
  }

  get button1(): TContentHubButton | undefined {
    return this.fields?.apiData?.button1;
  }

  get colHeadings(): Array<string> {
    return this.fields?.apiData?.models?.map((model) => model.title || '') || [];
  }

  get mrpTitle(): StringField {
    return { value: this.fields?.apiData?.msrpTitle || '' };
  }

  get mrpDescription(): StringField {
    return { value: this.fields?.apiData?.msrpDescription || '' };
  }

  get showTooltip(): boolean {
    return Boolean(this.fields?.apiData?.msrpTitle || this.fields?.apiData?.msrpDescription);
  }

  get notes(): TStringField {
    return { value: appendRootToRichTextInternalLinks(this.fields?.apiData?.notes) || '' };
  }

  get mrpRow(): TMRPRow {
    return {
      label: this.fields?.apiData?.msrpLabel || '',
      showTooltip: this.showTooltip,
      title: this.mrpTitle,
      description: this.mrpDescription,
      prices: this.fields?.apiData?.models?.map((model) => `${model.optionalLabel || ''} ${model.msrp || ''}`) || [],
      hideRow: false,
    };
  }

  get categoryRows(): Array<TSpecGroup> {
    const specs: TTableData = createSpecsObj(this.fields?.apiData?.models);

    if (this.fields?.apiData?.models?.length) {
      const newValues: Array<TSpecGroup> = [];
      const firstModelSpecs = this.fields?.apiData?.models[0].specs.map((spec) => {
        const items = spec.items.map((item) => ({ id: item.id }));
        return { group: spec.group, items };
      });

      firstModelSpecs.forEach(({ group, items }) => {
        const insertGroup = specs[group];
        insertGroup.items = [];
        items.forEach(({ id }) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          insertGroup.items.push(insertGroup.tempItems![id]);
        });
        newValues.push(insertGroup);
      });
      return newValues;
    }
    return [];
  }

  get exportData(): TExportData | undefined {
    return this.fields?.exportData;
  }

  get iconsList(): Array<TIconFromRenderingParams> {
    return this.fields?.itemData?.iconsList || [];
  }

  get isUnreleased(): boolean {
    return !!this.fields?.isComponentUnreleased;
  }

  get convertToExcelLink(): string {
    const specificationUrlParametersArray = [
      this.exportData?.id,
      this.exportData?.language,
      this.exportData?.lineId,
      this.exportData?.serieId,
    ];

    if (this.seriesDetail) {
      const specificationUrlParameters = specificationUrlParametersArray.join('/');
      return `/${APIPrefix}/download/serie-specifications/${specificationUrlParameters}`;
    }

    specificationUrlParametersArray.push(this.exportData?.modelId);
    const specificationUrlParameters = specificationUrlParametersArray.join('/');
    return `/${APIPrefix}/download/model-specifications/${specificationUrlParameters}`;
  }
}
